import React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Sparkline } from '@/components/charts/Sparkline';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Props for the MetricCard component
 */
export interface MetricCardProps {
  /**
   * Metric label/title
   */
  label: string;

  /**
   * Main metric value
   */
  value: string | number;

  /**
   * Optional unit (e.g., '$', '%', 'ms')
   */
  unit?: string;

  /**
   * Trend information
   */
  trend?: {
    /** Percentage change */
    value: number;
    /** Trend direction */
    direction: 'up' | 'down' | 'stable';
    /** Whether the trend is positive (green) or negative (red) */
    isPositive?: boolean;
  };

  /**
   * Comparison text (e.g., "vs last week")
   */
  comparison?: string;

  /**
   * Visual variant for different metric types
   * @default 'default'
   */
  variant?: 'default' | 'success' | 'danger' | 'warning' | 'neutral';

  /**
   * Optional sparkline data
   */
  sparkline?: number[];

  /**
   * Optional icon
   */
  icon?: React.ReactNode;

  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Loading state
   * @default false
   */
  loading?: boolean;

  /**
   * Additional metadata to display
   */
  metadata?: string | React.ReactNode;

  /**
   * Format function for the value
   */
  formatValue?: (value: string | number) => string;

  /**
   * Whether to show hover effects
   * @default true
   */
  hoverable?: boolean;

  /**
   * Click handler
   */
  onClick?: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Test ID for E2E testing
   */
  'data-testid'?: string;
}

const variantClasses = {
  default: {
    border: 'border-border',
    glow: 'hover:shadow-glow-primary',
    icon: 'text-primary',
    trend: {
      up: 'text-success',
      down: 'text-danger',
      stable: 'text-warning',
    },
  },
  success: {
    border: 'border-border',
    glow: 'hover:shadow-glow-success-combined',
    icon: 'text-success',
    trend: {
      up: 'text-success',
      down: 'text-danger',
      stable: 'text-warning',
    },
  },
  danger: {
    border: 'border-danger',
    glow: 'hover:shadow-glow-error-combined',
    icon: 'text-danger',
    trend: {
      up: 'text-success',
      down: 'text-danger',
      stable: 'text-warning',
    },
  },
  warning: {
    border: 'border-border',
    glow: 'hover:shadow-glow-primary',
    icon: 'text-warning',
    trend: {
      up: 'text-success',
      down: 'text-danger',
      stable: 'text-warning',
    },
  },
  neutral: {
    border: 'border-border',
    glow: 'hover:shadow-glow-primary',
    icon: 'text-fg-muted',
    trend: {
      up: 'text-success',
      down: 'text-danger',
      stable: 'text-warning',
    },
  },
};

const sizeClasses = {
  sm: {
    padding: 'p-3',
    minHeight: 'min-h-[120px]',
    value: 'text-lg',
    label: 'text-xs',
    icon: 'h-4 w-4',
    trend: 'text-xs',
    sparkline: { width: 60, height: 30 },
  },
  md: {
    padding: 'p-4',
    minHeight: 'min-h-[140px]',
    value: 'text-2xl',
    label: 'text-sm',
    icon: 'h-5 w-5',
    trend: 'text-sm',
    sparkline: { width: 80, height: 40 },
  },
  lg: {
    padding: 'p-5',
    minHeight: 'min-h-[160px]',
    value: 'text-3xl',
    label: 'text-base',
    icon: 'h-6 w-6',
    trend: 'text-base',
    sparkline: { width: 100, height: 50 },
  },
};

/**
 * Get the trend icon based on direction
 */
const getTrendIcon = (direction: 'up' | 'down' | 'stable') => {
  switch (direction) {
    case 'up':
      return TrendingUp;
    case 'down':
      return TrendingDown;
    case 'stable':
      return Minus;
    default:
      return null;
  }
};

/**
 * MetricCard Component
 *
 * Simplified metric display card with Bloomberg Terminal aesthetics.
 * Shows a key metric with optional trend, sparkline, and comparison.
 *
 * @example
 * ```tsx
 * <MetricCard
 *   label="Active Users"
 *   value={12459}
 *   unit="users"
 *   trend={{ value: 12.5, direction: 'up', isPositive: true }}
 *   comparison="vs last week"
 *   variant="success"
 *   sparkline={[100, 120, 115, 130, 125, 140, 150]}
 * />
 * ```
 */
export const MetricCard = React.memo<MetricCardProps>(
  ({
    label,
    value,
    unit,
    trend,
    comparison,
    variant = 'default',
    sparkline,
    icon,
    size = 'md',
    loading = false,
    metadata,
    formatValue,
    hoverable = true,
    onClick,
    className,
    'data-testid': testId,
  }) => {
    const variantConfig = variantClasses[variant];
    const sizeConfig = sizeClasses[size];

    // Format the display value
    const displayValue = formatValue ? formatValue(value) : value;

    const TrendIcon = trend ? getTrendIcon(trend.direction) : null;

    // Loading state
    if (loading) {
      return (
        <div
          className={cn(
            'bg-bg-elevated rounded-lg border border-border',
            sizeConfig.padding,
            className
          )}
        >
          <div className="space-y-2">
            <Skeleton variant="text" width="40%" height={14} />
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="30%" height={12} />
          </div>
        </div>
      );
    }

    const isClickable = Boolean(onClick);

    return (
      <div
        data-testid={testId}
        className={cn(
          'bg-bg-elevated rounded-lg border',
          variantConfig.border,
          'shadow-md',
          'transition-all duration-150',
          hoverable && 'hover:border-opacity-50',
          hoverable && variantConfig.glow,
          isClickable && 'cursor-pointer',
          sizeConfig.padding,
          sizeConfig.minHeight,
          'relative overflow-hidden',
          'h-full flex flex-col',
          hoverable && 'metric-interactive',
          className
        )}
        onClick={onClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
      >
        {/* Subtle background gradient */}
        <div className="absolute inset-0 bg-linear-to-br from-transparent to-primary/5 pointer-events-none" />

        <div className="relative flex-1 flex flex-col">
          {/* Header with icon and label */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              {icon && (
                <span className={cn(variantConfig.icon, sizeConfig.icon)}>{icon}</span>
              )}
              <span className={cn('metric-label', size === 'lg' && 'metric-label-lg')}>
                {label}
              </span>
            </div>

            {/* Trend badge */}
            {trend && (
              <Badge
                variant={trend.isPositive ? 'success' : 'danger'}
                size="xs"
                className="ml-auto"
              >
                {TrendIcon && <TrendIcon className="h-3 w-3" />}
                <span className="ml-1">
                  {trend.value > 0 && '+'}
                  {trend.value.toFixed(1)}%
                </span>
              </Badge>
            )}
          </div>

          {/* Main value and sparkline */}
          <div className="flex items-end justify-between">
            <div className="flex-1 min-w-0">
              {/* Value */}
              <div
                className={cn(
                  'font-metric font-light tabular-nums',
                  size === 'lg'
                    ? 'metric-value-sm'
                    : size === 'md'
                      ? 'metric-value-md'
                      : 'text-lg',
                  variant === 'success' && 'metric-value-success',
                  variant === 'danger' && 'metric-value-danger',
                  variant === 'warning' && 'metric-value-warning',
                  variant === 'default' && 'metric-value-primary'
                )}
              >
                {unit && <span className="metric-unit">{unit}</span>}
                <span>{displayValue}</span>
              </div>

              {/* Comparison text */}
              {comparison && (
                <div className="text-xs text-fg-muted mt-1">{comparison}</div>
              )}

              {/* Additional metadata */}
              {metadata && <div className="text-xs text-fg-subtle mt-1">{metadata}</div>}
            </div>

            {/* Sparkline - hidden on mobile for space */}
            {sparkline && sparkline.length > 0 && (
              <div className="ml-4 opacity-70 hover:opacity-100 transition-opacity hidden sm:block">
                <Sparkline
                  data={sparkline}
                  width={sizeConfig.sparkline.width}
                  height={sizeConfig.sparkline.height}
                  color={
                    variant === 'success'
                      ? 'success'
                      : variant === 'danger'
                        ? 'danger'
                        : 'primary'
                  }
                  type="line"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
);

/**
 * MetricCardGroup Component
 *
 * Helper component for grouping multiple metric cards
 */
export const MetricCardGroup = React.memo<{
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4 | 'auto';
  gap?: 'sm' | 'md' | 'lg';
  className?: string;
}>(({ children, columns = 'auto', gap = 'md', className }) => {
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
    // Mobile: 1 col | Tablet: 2 cols | Desktop (1280px+): 3 cols | Wide (1536px+): 4 cols
    auto: 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
  };

  const gapClasses = {
    sm: 'gap-3',
    md: 'gap-4',
    lg: 'gap-6',
  };

  return (
    <div className={cn('grid', columnClasses[columns], gapClasses[gap], className)}>
      {children}
    </div>
  );
});

/**
 * MetricCardSkeleton Component
 *
 * Skeleton loader for MetricCard
 */
export const MetricCardSkeleton = React.memo<{
  size?: 'sm' | 'md' | 'lg';
  showSparkline?: boolean;
  className?: string;
}>(({ size = 'md', showSparkline = false, className }) => {
  const sizeConfig = sizeClasses[size];

  return (
    <div
      className={cn(
        'bg-bg-elevated rounded-lg border border-border',
        sizeConfig.padding,
        className
      )}
    >
      <div className="space-y-2">
        {/* Label */}
        <Skeleton variant="text" width="40%" height={14} />

        {/* Value and sparkline row */}
        <div className="flex items-end justify-between">
          <div className="flex-1">
            {/* Value */}
            <Skeleton
              variant="text"
              width="60%"
              height={size === 'lg' ? 32 : size === 'md' ? 28 : 24}
            />
            {/* Comparison */}
            <Skeleton variant="text" width="30%" height={12} className="mt-1" />
          </div>

          {/* Sparkline */}
          {showSparkline && (
            <Skeleton
              variant="rectangular"
              width={sizeConfig.sparkline.width}
              height={sizeConfig.sparkline.height}
              className="ml-4"
            />
          )}
        </div>
      </div>
    </div>
  );
});
